import type { Logger } from "@expert/logging";

import { DraggableComponent } from "@expert/solve-tools";
import { Button, Flex, Group, Textarea, Transition } from "@mantine/core";
import { useState } from "react";

import type { ColorGroup } from "../../state";

import { ColorPalette } from "../../assets";
import { noteColorGroups, useExpertNoteStore } from "../../state";
import { ColorCircles } from "./ColorCircles";
import classes from "./Notepad.module.css";

interface NotepadProps {
    opened: boolean;
    logger: Logger;
    onClose: VoidFunction;
}

const placeholder =
    "Use this as a personal scratchpad—it’ll reset when you log out and won’t save to customer accounts. Never enter PII.";

export function Notepad({ opened, onClose, logger }: NotepadProps) {
    const {
        clearNote,
        color,
        note,
        notepadLocation,
        notepadSize,
        setColor,
        setNote,
        setNotepadLocation,
        setNotepadSize,
    } = useExpertNoteStore();

    const backgroundColor = color[0];
    const textColor = color[1];
    const scrollbarColor = color[2];
    const [isColorPaletteOpened, setIsColorPaletteOpened] = useState(false);
    // Clearing takes two clicks so a stray click can't wipe the expert's notes.
    const [isConfirmingClear, setIsConfirmingClear] = useState(false);

    const onChangeText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNote(event.currentTarget.value);
        // Editing means the expert moved on; without this, emptying and retyping the note would bring
        // the button back already armed, so one click would clear it.
        setIsConfirmingClear(false);
    };
    const handleSetColor = (colorGroup: ColorGroup) => {
        logger.child({ module: "Notepad" }).info({ color: colorGroup[0] }, "Notepad color changed");
        setColor(colorGroup);
        setIsColorPaletteOpened(false);
    };
    const handleClear = () => {
        if (!isConfirmingClear) {
            setIsConfirmingClear(true);
            return;
        }
        clearNote(logger.child({ module: "Notepad" }));
        setIsConfirmingClear(false);
    };
    const cancelClear = () => {
        setIsConfirmingClear(false);
    };

    return (
        <DraggableComponent
            backgroundColor={backgroundColor}
            iconColor={textColor}
            location={notepadLocation}
            logger={logger}
            onClose={onClose}
            opened={opened}
            setLocation={setNotepadLocation}
            setSize={setNotepadSize}
            size={notepadSize}
            testId="notepad"
            title="Notepad"
        >
            <Textarea
                className="fs-mask"
                classNames={{ input: classes.input }}
                data-testid="notepad-textarea"
                minRows={10}
                onChange={onChangeText}
                placeholder={placeholder}
                styles={{
                    input: {
                        "--notepad-placeholder-color": textColor,
                        "--notepad-scrollbar-color": scrollbarColor,
                        backgroundColor,
                        color: textColor,
                    },
                    root: {
                        display: "flex",
                        height: "100%",
                        width: "100%",
                        marginBottom: "0.5rem",
                    },
                    wrapper: { width: "100%" },
                }}
                value={note}
            />
            <Flex align="center" className={classes.footer} justify="space-between">
                <Group className={classes.colorPaletteGrid}>
                    <Transition
                        duration={200}
                        mounted={!isColorPaletteOpened}
                        timingFunction="linear"
                        transition="fade"
                    >
                        {(styles) => (
                            <Flex
                                className={`${classes.colorPaletteRow} ${classes.layer1}`}
                                data-testid="notepad-color-palette-icon"
                                style={styles}
                            >
                                <ColorPalette
                                    className={classes.colorPaletteIcon}
                                    cursor="pointer"
                                    fill={textColor}
                                    onClick={() => {
                                        setIsColorPaletteOpened(true);
                                    }}
                                />
                            </Flex>
                        )}
                    </Transition>
                    <Transition duration={200} mounted={isColorPaletteOpened} timingFunction="linear" transition="fade">
                        {(styles) => (
                            <Flex className={`${classes.colorPaletteRow} ${classes.layer2}`} style={styles}>
                                <ColorCircles colors={noteColorGroups} onSetColor={handleSetColor} />
                            </Flex>
                        )}
                    </Transition>
                </Group>
                {/* Only shown when there's something to clear; the open palette spans the notepad's width. */}
                {note && !isColorPaletteOpened && (
                    <Button
                        c={textColor}
                        data-testid="notepad-clear-button"
                        onBlur={cancelClear}
                        onClick={handleClear}
                        onMouseLeave={cancelClear}
                        size="compact-xs"
                        variant="transparent"
                    >
                        {isConfirmingClear ? "Click again to clear" : "Clear notes"}
                    </Button>
                )}
            </Flex>
        </DraggableComponent>
    );
}
